import * as THREE from 'three';
import { RARITY_COLORS, RARITY_LABELS, type WeaponRarity } from './FpsWeaponRarity.js';

export const RARITY_BEAM_COLORS: Record<WeaponRarity, number> = {
  common: 0x888888,
  uncommon: 0x00cc44,
  rare: 0x4488ff,
  epic: 0xaa44ff,
  legendary: 0xffaa00,
};

export const RARITY_BEAM_HEIGHTS: Record<WeaponRarity, number> = {
  common: 4,
  uncommon: 6,
  rare: 8,
  epic: 10,
  legendary: 13,
};

export type RarityBeam = {
  mesh: THREE.Mesh;
  rarity: WeaponRarity;
  x: number;
  z: number;
  pulseT: number;
};

export class FpsLootRarity {
  private beams: RarityBeam[] = [];
  private tooltip!: HTMLDivElement;
  private tooltipVisible = false;
  private tooltipTimer = 0;
  private readonly scene: THREE.Scene;

  constructor(scene: THREE.Scene, mount: HTMLElement) {
    this.scene = scene;

    this.tooltip = document.createElement('div');
    Object.assign(this.tooltip.style, {
      position: 'fixed',
      bottom: '38%',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'rgba(8,12,22,0.88)',
      borderLeft: '3px solid #fff',
      padding: '6px 14px 6px 10px',
      borderRadius: '4px',
      fontFamily: 'sans-serif',
      fontSize: '13px',
      color: '#eee',
      pointerEvents: 'none',
      zIndex: '9999',
      display: 'none',
      minWidth: '140px',
      lineHeight: '1.5',
    });
    mount.appendChild(this.tooltip);
  }

  spawnBeam(x: number, y: number, z: number, rarity: WeaponRarity): RarityBeam {
    const height = RARITY_BEAM_HEIGHTS[rarity];
    const color = RARITY_BEAM_COLORS[rarity];
    const geo = new THREE.CylinderGeometry(0.06, 0.06, height, 8, 1, true);
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.55,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y + height / 2, z);
    this.scene.add(mesh);

    const beam: RarityBeam = { mesh, rarity, x, z, pulseT: 0 };
    this.beams.push(beam);
    return beam;
  }

  removeBeam(beam: RarityBeam): void {
    beam.mesh.removeFromParent();
    beam.mesh.geometry.dispose();
    (beam.mesh.material as THREE.Material).dispose();
    const idx = this.beams.indexOf(beam);
    if (idx >= 0) this.beams.splice(idx, 1);
  }

  showTooltip(name: string, rarity: WeaponRarity, stats: string): void {
    const color = RARITY_COLORS[rarity];
    const label = RARITY_LABELS[rarity];
    this.tooltip.style.borderLeftColor = color;
    this.tooltip.innerHTML = `
      <div style="font-weight:bold;color:#fff;font-size:14px;">${name}</div>
      <div style="color:${color};font-size:12px;">${label}</div>
      <div style="color:#aaa;font-size:11px;">${stats}</div>
    `;
    this.tooltip.style.display = 'block';
    this.tooltipVisible = true;
    this.tooltipTimer = 2.0;
  }

  hideTooltip(): void {
    this.tooltip.style.display = 'none';
    this.tooltipVisible = false;
    this.tooltipTimer = 0;
  }

  update(dt: number): void {
    for (const beam of this.beams) {
      beam.pulseT += dt;
      beam.mesh.rotation.y += dt * 0.8;
      const mat = beam.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.4 + Math.sin(beam.pulseT * 3) * 0.2;
    }

    if (this.tooltipVisible && this.tooltipTimer > 0) {
      this.tooltipTimer -= dt;
      if (this.tooltipTimer <= 0) {
        this.hideTooltip();
      }
    }
  }

  dispose(): void {
    for (const beam of this.beams) {
      beam.mesh.removeFromParent();
      beam.mesh.geometry.dispose();
      (beam.mesh.material as THREE.Material).dispose();
    }
    this.beams.length = 0;
    this.tooltip.remove();
  }
}
